/**
 * The upload envelope: what an attachment may be, and how big.
 *
 * **One module, two consumers, on purpose.** This used to be two lists — one in
 * `packages/widget/src/attachments/mime.ts` for fail-fast UX, one in
 * `apps/api/src/ingest/ingest.service.ts` as the enforcement point — with a
 * comment on the widget's copy saying it MUST match the backend's exactly. Two
 * lists that must match are a list that will not: the same failure ADR 0004
 * records for the parallel component trees. The backend still re-validates
 * independently, which is the property that mattered; it just no longer
 * re-STATES the values.
 */

/**
 * MIME types an attachment may carry, AFTER normalisation.
 *
 * A SAS URL cannot constrain the uploaded Content-Type (see the note in
 * `AzureBlobStorage.getUploadUrl`), so the ingest layer is the enforcement
 * point regardless of what the browser claimed.
 *
 * `application/octet-stream` is deliberately NOT here. It is the type a browser
 * reports when it recognises nothing, so admitting it would admit everything —
 * see `normalizeContentType`, which resolves the octet-stream case by extension
 * and leaves anything it cannot place as octet-stream so this list rejects it.
 */
export const ALLOWED_UPLOAD_MIME_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  // Diagnostics shapes. A desktop reporter attaches logs, a HAR capture and
  // saved mail; none of these is a browser-registered type on a clean machine.
  'application/json',
  'message/rfc822',
  'application/vnd.ms-outlook',
  // The only format `MediaRecorder` produces in every browser that has the API
  // at all, so admitting it is what lets a reporter record their screen
  // (I19222, ADR 0030). Deliberately the only moving-image type here: a file a
  // reporter picked off disk is still governed by the list above, and a
  // container this product cannot play back is not worth the storage.
  'video/webm',
];

/**
 * 10 MB. Keep in sync with the widget's `tooLarge` wording.
 *
 * Also the ceiling a screen recording stops itself at. Sharing one number is
 * the point rather than a convenience: bytes go browser to blob against a
 * 15-minute SAS, and a recording that outgrew the envelope would need chunking
 * and a SAS refresh, which is a second upload path that only ever runs for
 * large files and is therefore the path least exercised.
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * How long a screen recording may run before the recorder stops itself.
 *
 * Enforced at capture rather than at upload, because a reporter who records for
 * two minutes and is only then told the file is too large has been made to do
 * the work twice, and the second attempt is the one where they give up
 * (ADR 0030).
 */
export const MAX_RECORDING_SECONDS = 30;

/**
 * Extensions a browser routinely fails to type, and what they actually are.
 *
 * The problem this solves: a browser reports an empty `type`, or
 * `application/octet-stream`, for any extension the OS has no registration
 * for. On a clean Windows machine that is `.log`, `.md`, `.json`, `.har`,
 * `.msg` and `.eml` — which is to say the entire contents of a diagnostics
 * bundle. The upload was refused at the door for having no type rather than
 * for being the wrong one.
 *
 * `.har` maps to `application/json` because that is what a HAR file IS; giving
 * it a bespoke type would mean a second entry in the allow-list for a format
 * that is already covered.
 */
const EXTENSION_MIME_MAP: Readonly<Record<string, string>> = {
  log: 'text/plain',
  md: 'text/plain',
  txt: 'text/plain',
  json: 'application/json',
  har: 'application/json',
  eml: 'message/rfc822',
  msg: 'application/vnd.ms-outlook',
  // A recording the widget made always arrives with its type set, because the
  // blob comes from `MediaRecorder` rather than from a disk. This entry is for
  // the other direction: a reporter who saved that file and attached it later,
  // on a machine with no registration for the extension.
  webm: 'video/webm',
};

/** The types that mean "the browser recognised nothing", not "this is binary". */
const UNTYPED = new Set(['', 'application/octet-stream']);

/**
 * Resolves what a file actually is, when the browser could not say.
 *
 * A reported type always wins. Extension sniffing is a fallback for the
 * unregistered case, never a second opinion on a type the browser did resolve,
 * because the reported type is the better signal whenever it exists.
 *
 * An extension this does not know stays `application/octet-stream`, so
 * normalisation can never be a way IN — {@link isAllowedUpload} rejects it
 * exactly as it did before this existed.
 */
export function normalizeContentType(contentType: string, fileName: string): string {
  const reported = contentType.trim().toLowerCase();
  if (!UNTYPED.has(reported)) return reported;

  // `lastIndexOf`, and it must be past position 0: a dotfile like `.log` is a
  // NAME beginning with a dot, not a file with a `log` extension.
  const dot = fileName.lastIndexOf('.');
  if (dot < 1) return 'application/octet-stream';

  const extension = fileName.slice(dot + 1).toLowerCase();
  return EXTENSION_MIME_MAP[extension] ?? 'application/octet-stream';
}

/** Whether an attachment may be accepted, after resolving its real type. */
export function isAllowedUpload(contentType: string, fileName: string): boolean {
  return ALLOWED_UPLOAD_MIME_TYPES.includes(normalizeContentType(contentType, fileName));
}
